/**
 * Per-tile runtime state (S3) — status, last success, rolling durations.
 * NEVER persisted. Writes are guarded by executionId: only the NEWEST
 * execution for a tile may settle its state; older resolutions are discarded
 * (out-of-order network responses must not clobber fresher data), and a tile
 * removed mid-flight settles into nothing (no state write).
 *
 * `settle` reports whether it applied the result, and that verdict is the
 * ONE arbiter for every other sink too: the TanStack tile cache mirrors the
 * runtime store, so a run this guard discards must not reach the cache
 * either (`useTileResult` / `runTileManually`). Overlapping manual and
 * scheduler runs of one tile land through different paths — a late older
 * result that bypassed this verdict used to overwrite the newer one's data.
 */
import { create } from 'zustand';
import type { DashboardTileRuntime, TileRunResult } from '../types/dashboardRuntime';

const MAX_ROLLING_DURATIONS = 10;

export const tileRuntimeKey = (dashboardId: string, tileId: string) => `${dashboardId}/${tileId}`;
const tileKey = tileRuntimeKey;

type DashboardRuntimeStoreState = {
  byTile: Record<string, DashboardTileRuntime>;
  /** Marks the newest execution for a tile; supersedes older in-flight runs. */
  begin: (dashboardId: string, tileId: string, executionId: string) => void;
  /**
   * Applies a run result IF executionId is still the newest for the tile.
   * Returns whether it was applied — `false` means a newer execution began
   * since (or the tile was removed), and the caller must drop the result
   * everywhere, including the TanStack tile cache.
   */
  settle: (
    dashboardId: string,
    tileId: string,
    executionId: string,
    result: TileRunResult,
  ) => boolean;
  markStale: (
    dashboardId: string,
    tileId: string,
    reason: NonNullable<DashboardTileRuntime['staleReason']>,
  ) => void;
  clearTile: (dashboardId: string, tileId: string) => void;
  clearDashboard: (dashboardId: string) => void;
};

export const useDashboardRuntimeStore = create<DashboardRuntimeStoreState>((set, get) => ({
  byTile: {},

  begin: (dashboardId, tileId, executionId) =>
    set((s) => {
      const key = tileKey(dashboardId, tileId);
      const prev = s.byTile[key];
      return {
        byTile: {
          ...s.byTile,
          [key]: {
            ...(prev ?? { durationsMs: [] }),
            status: 'running',
            executionId,
          },
        },
      };
    }),

  settle: (dashboardId, tileId, executionId, result) => {
    const key = tileKey(dashboardId, tileId);
    const prev = get().byTile[key];
    // Removed mid-flight (no entry) or superseded (newer executionId
    // began since) — discard, write nothing, tell the caller.
    if (!prev || prev.executionId !== executionId) return false;
    const next: DashboardTileRuntime = result.ok
      ? {
          status: 'success',
          executionId,
          lastSuccess: { result, at: new Date().toISOString() },
          durationsMs: [...prev.durationsMs, result.durationMs].slice(-MAX_ROLLING_DURATIONS),
        }
      : {
          ...prev,
          executionId,
          error: result.error,
          ...(result.needsConnection
            ? { status: 'stale', staleReason: 'connection-lost' }
            : result.blockedByPolicy
              ? { status: 'stale', staleReason: 'policy-blocked', error: result.blockedByPolicy }
              : { status: 'error', staleReason: undefined }),
        };
    set((s) => ({ byTile: { ...s.byTile, [key]: next } }));
    return true;
  },

  markStale: (dashboardId, tileId, reason) =>
    set((s) => {
      const key = tileKey(dashboardId, tileId);
      const prev = s.byTile[key];
      if (!prev) return s;
      return { byTile: { ...s.byTile, [key]: { ...prev, status: 'stale', staleReason: reason } } };
    }),

  clearTile: (dashboardId, tileId) =>
    set((s) => {
      const key = tileKey(dashboardId, tileId);
      if (!(key in s.byTile)) return s;
      const byTile = { ...s.byTile };
      delete byTile[key];
      return { byTile };
    }),

  clearDashboard: (dashboardId) =>
    set((s) => {
      const prefix = `${dashboardId}/`;
      const byTile = Object.fromEntries(
        Object.entries(s.byTile).filter(([key]) => !key.startsWith(prefix)),
      );
      return { byTile };
    }),
}));

/** Read helper for non-React callers (scheduler wiring, snapshot bridge). */
export const getTileRuntime = (
  dashboardId: string,
  tileId: string,
): DashboardTileRuntime | undefined =>
  useDashboardRuntimeStore.getState().byTile[tileKey(dashboardId, tileId)];

export const isTileRunning = (dashboardId: string, tileId: string): boolean =>
  getTileRuntime(dashboardId, tileId)?.status === 'running';

export const selectTileRuntime =
  (dashboardId: string, tileId: string) =>
  (s: DashboardRuntimeStoreState): DashboardTileRuntime | undefined =>
    s.byTile[tileKey(dashboardId, tileId)];
